import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './admin.css';
import { Card, Col, Row, Typography, Select, Space, Progress, Tag, Table, Button, DatePicker, Tooltip } from 'antd';
import { RobotOutlined, ReloadOutlined, ArrowUpOutlined, ArrowDownOutlined, EyeOutlined, ThunderboltOutlined, DollarOutlined, GlobalOutlined } from '@ant-design/icons';
import { API_BASE, authHeaders } from '../config/api';
import LiveExperienceScore from './LiveExperienceScore';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// ─── CHART UTILITIES ─────────────────────────────────────────────────────────

const PALETTE = {
  blue:   ['#2563eb','#3b82f6','#93c5fd','#dbeafe'],
  green:  ['#16a34a','#22c55e','#86efac','#dcfce7'],
  purple: ['#7c3aed','#8b5cf6','#c4b5fd','#ede9fe'],
  amber:  ['#d97706','#f59e0b','#fcd34d','#fef3c7'],
  rose:   ['#e11d48','#f43f5e','#fda4af','#ffe4e6'],
  cyan:   ['#0891b2','#06b6d4','#67e8f9','#cffafe'],
};
const SERIES_COLORS = [PALETTE.blue[1], PALETTE.green[1], PALETTE.purple[1], PALETTE.amber[1], PALETTE.rose[1], PALETTE.cyan[1]];

// ─── ANIMATED AREA/LINE CHART ─────────────────────────────────────────────────
const AreaChart = ({ series = [], labels = [], height = 220, showGrid = true, showDots = true, gradient = true }) => {
  const ref = useRef(null);
  const raf = useRef(null);
  const prog = useRef(0);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.clientWidth;
    const H = height;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    ctx.scale(dpr, dpr);

    const PAD = { top: 16, right: 16, bottom: 32, left: 44 };
    const iW = W - PAD.left - PAD.right;
    const iH = H - PAD.top - PAD.bottom;

    const allVals = series.flatMap(s => s.data);
    const minY = Math.min(0, ...allVals);
    const maxY = Math.max(1, ...allVals);
    const range = maxY - minY || 1;
    const n = Math.max(2, labels.length);

    const px = i => PAD.left + (i / (n - 1)) * iW;
    const py = v => PAD.top + iH - ((v - minY) / range) * iH;

    const draw = (pct) => {
      ctx.clearRect(0, 0, W, H);

      // Grid
      if (showGrid) {
        ctx.strokeStyle = 'rgba(148,163,184,0.15)';
        ctx.lineWidth = 1;
        const ticks = 5;
        for (let i = 0; i <= ticks; i++) {
          const y = PAD.top + (iH / ticks) * i;
          ctx.beginPath(); ctx.moveTo(PAD.left, y); ctx.lineTo(W - PAD.right, y); ctx.stroke();
          const val = maxY - (range / ticks) * i;
          ctx.fillStyle = 'rgba(100,116,139,0.8)';
          ctx.font = '10px system-ui';
          ctx.textAlign = 'right';
          ctx.fillText(val >= 1000 ? `${(val/1000).toFixed(1)}k` : Math.round(val), PAD.left - 6, y + 4);
        }
        // X labels
        ctx.textAlign = 'center';
        labels.forEach((lbl, i) => {
          const step = Math.max(1, Math.floor(n / 7));
          if (i % step !== 0 && i !== n - 1) return;
          ctx.fillStyle = 'rgba(100,116,139,0.8)';
          ctx.fillText(lbl, px(i), H - PAD.bottom + 16);
        });
      }

      // Series
      series.forEach((s, si) => {
        if (!s.data || s.data.length < 2) return;
        const color = s.color || SERIES_COLORS[si % SERIES_COLORS.length];
        const pts = s.data.map((v, i) => [px(i), py(v)]);
        const cutIdx = Math.floor((pts.length - 1) * pct);

        // Build clipped path
        ctx.save();
        ctx.beginPath();
        ctx.rect(PAD.left, PAD.top - 2, iW * pct, iH + 4);
        ctx.clip();

        // Gradient fill
        if (gradient) {
          const fillGrad = ctx.createLinearGradient(0, PAD.top, 0, PAD.top + iH);
          fillGrad.addColorStop(0, `${color}55`);
          fillGrad.addColorStop(1, `${color}00`);
          ctx.beginPath();
          ctx.moveTo(pts[0][0], PAD.top + iH);
          pts.forEach(([x, y]) => ctx.lineTo(x, y));
          ctx.lineTo(pts[pts.length - 1][0], PAD.top + iH);
          ctx.closePath();
          ctx.fillStyle = fillGrad;
          ctx.fill();
        }

        // Line
        ctx.beginPath();
        pts.forEach(([x, y], i) => i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y));
        ctx.strokeStyle = color;
        ctx.lineWidth = 2.5;
        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';
        ctx.shadowColor = color;
        ctx.shadowBlur = 8;
        ctx.stroke();
        ctx.shadowBlur = 0;

        // Dots
        if (showDots) {
          pts.slice(0, cutIdx + 1).forEach(([x, y]) => {
            ctx.beginPath();
            ctx.arc(x, y, 4, 0, Math.PI * 2);
            ctx.fillStyle = '#fff';
            ctx.fill();
            ctx.strokeStyle = color;
            ctx.lineWidth = 2;
            ctx.stroke();
          });
        }
        ctx.restore();
      });
    };

    prog.current = 0;
    const animate = () => {
      prog.current = Math.min(1, prog.current + 0.025);
      draw(prog.current);
      if (prog.current < 1) raf.current = requestAnimationFrame(animate);
    };
    animate();
    return () => cancelAnimationFrame(raf.current);
  }, [series, labels, height, showGrid, showDots, gradient]);

  // Legend
  return (
    <div>
      {series.length > 1 && (
        <div style={{ display: 'flex', gap: 16, marginBottom: 10, flexWrap: 'wrap' }}>
          {series.map((s, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
              <div style={{ width: 20, height: 3, borderRadius: 2, background: s.color || SERIES_COLORS[i] }} />
              <span style={{ color: '#475569' }}>{s.name}</span>
            </div>
          ))}
        </div>
      )}
      <canvas ref={ref} style={{ width: '100%', height }} />
    </div>
  );
};

// ─── STACKED BAR CHART ───────────────────────────────────────────────────────
const StackedBarChart = ({ data = [], keys = [], colors = SERIES_COLORS, height = 200, labels = [] }) => {
  const ref = useRef(null);
  const raf = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.clientWidth;
    const H = height;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    ctx.scale(dpr, dpr);

    const PAD = { top: 12, right: 16, bottom: 28, left: 44 };
    const iW = W - PAD.left - PAD.right;
    const iH = H - PAD.top - PAD.bottom;
    const totals = data.map(d => keys.reduce((s, k) => s + (d[k] || 0), 0));
    const maxVal = Math.max(1, ...totals);
    const n = data.length;
    const barW = (iW / n) * 0.55;
    const gap = iW / n;

    let prog = 0;
    const animate = () => {
      prog = Math.min(1, prog + 0.03);
      ctx.clearRect(0, 0, W, H);

      // Grid
      const ticks = 4;
      for (let i = 0; i <= ticks; i++) {
        const y = PAD.top + (iH / ticks) * i;
        ctx.strokeStyle = 'rgba(148,163,184,0.15)';
        ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(PAD.left, y); ctx.lineTo(W - PAD.right, y); ctx.stroke();
        const val = maxVal - (maxVal / ticks) * i;
        ctx.fillStyle = 'rgba(100,116,139,0.8)';
        ctx.font = '10px system-ui';
        ctx.textAlign = 'right';
        ctx.fillText(val >= 1000 ? `${(val/1000).toFixed(1)}k` : Math.round(val), PAD.left - 6, y + 4);
      }

      data.forEach((d, i) => {
        const x = PAD.left + i * gap + gap / 2 - barW / 2;
        let y = PAD.top + iH;
        keys.forEach((k, ki) => {
          const val = (d[k] || 0) * prog;
          const barH = (val / maxVal) * iH;
          const color = colors[ki % colors.length];
          ctx.fillStyle = color;
          ctx.beginPath();
          const r = ki === keys.length - 1 ? 4 : 0;
          ctx.roundRect(x, y - barH, barW, barH, [r, r, 0, 0]);
          ctx.fill();
          y -= barH;
        });
        // Label
        ctx.fillStyle = 'rgba(100,116,139,0.8)';
        ctx.font = '10px system-ui';
        ctx.textAlign = 'center';
        ctx.fillText(labels[i] || i, PAD.left + i * gap + gap / 2, H - PAD.bottom + 16);
      });

      if (prog < 1) raf.current = requestAnimationFrame(animate);
    };
    animate();
    return () => cancelAnimationFrame(raf.current);
  }, [data, keys, colors, height, labels]);

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
        {keys.map((k, i) => (
          <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: colors[i] }} />
            <span style={{ color: '#475569', textTransform: 'capitalize' }}>{k}</span>
          </div>
        ))}
      </div>
      <canvas ref={ref} style={{ width: '100%', height }} />
    </div>
  );
};

// ─── DONUT CHART ─────────────────────────────────────────────────────────────
const DonutChart = ({ data = [], size = 160, thickness = 0.35, showLabels = true }) => {
  const ref = useRef(null);
  const raf = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    ctx.scale(dpr, dpr);

    const cx = size / 2, cy = size / 2;
    const r = size / 2 - 10;
    const inner = r * (1 - thickness);
    const total = data.reduce((s, d) => s + Math.max(0, d.value), 0) || 1;

    let prog = 0;
    const animate = () => {
      prog = Math.min(1, prog + 0.04);
      ctx.clearRect(0, 0, size, size);

      let start = -Math.PI / 2;
      data.forEach((slice) => {
        const sweep = (Math.max(0, slice.value) / total) * Math.PI * 2 * prog;
        const color = slice.color || '#3b82f6';

        // Shadow
        ctx.shadowColor = `${color}44`;
        ctx.shadowBlur = 12;

        ctx.beginPath();
        ctx.arc(cx, cy, r, start, start + sweep);
        ctx.arc(cx, cy, inner, start + sweep, start, true);
        ctx.closePath();
        ctx.fillStyle = color;
        ctx.fill();
        ctx.shadowBlur = 0;

        // White separator
        ctx.beginPath();
        ctx.arc(cx, cy, r, start, start + sweep);
        ctx.arc(cx, cy, inner, start + sweep, start, true);
        ctx.closePath();
        ctx.strokeStyle = 'rgba(255,255,255,0.3)';
        ctx.lineWidth = 1.5;
        ctx.stroke();

        start += sweep;
      });

      // Center bg
      const centerGrad = ctx.createRadialGradient(cx, cy - 8, 0, cx, cy, inner);
      centerGrad.addColorStop(0, 'rgba(248,250,252,0.05)');
      centerGrad.addColorStop(1, 'rgba(15,23,42,0.0)');
      ctx.beginPath();
      ctx.arc(cx, cy, inner - 1, 0, Math.PI * 2);
      ctx.fillStyle = centerGrad;
      ctx.fill();

      if (prog < 1) raf.current = requestAnimationFrame(animate);
    };
    animate();
    return () => cancelAnimationFrame(raf.current);
  }, [data, size, thickness]);

  const total = data.reduce((s, d) => s + Math.max(0, d.value), 0);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
      <div style={{ position: 'relative', flexShrink: 0 }}>
        <canvas ref={ref} style={{ width: size, height: size }} />
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <div style={{ fontSize: 20, color: '#0f172a' }}>{total >= 1000 ? `${(total/1000).toFixed(1)}k` : total}</div>
          <div style={{ fontSize: 10, color: '#64748b' }}>Total</div>
        </div>
      </div>
      {showLabels && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {data.map((d, i) => {
            const pct = total > 0 ? ((d.value / total) * 100).toFixed(1) : 0;
            return (
              <div key={i}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3, fontSize: 12 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: d.color, flexShrink: 0 }} />
                    <span style={{ color: '#475569' }}>{d.label}</span>
                  </span>
                  <span style={{ fontWeight: 400, color: '#0f172a' }}>{pct}%</span>
                </div>
                <div style={{ height: 4, background: '#f1f5f9', borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${pct}%`, background: d.color, borderRadius: 2, transition: 'width 0.8s ease' }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── SPARKLINE ───────────────────────────────────────────────────────────────
const Sparkline = ({ points = [], color = '#3b82f6', height = 40, fill = true }) => {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || points.length < 2) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.clientWidth;
    const H = height;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    ctx.scale(dpr, dpr);

    const min = Math.min(...points);
    const max = Math.max(...points);
    const range = max - min || 1;
    const n = points.length;
    const px = i => (i / (n - 1)) * W;
    const py = v => H - ((v - min) / range) * (H - 4) - 2;

    if (fill) {
      const grad = ctx.createLinearGradient(0, 0, 0, H);
      grad.addColorStop(0, `${color}55`);
      grad.addColorStop(1, `${color}00`);
      ctx.beginPath();
      ctx.moveTo(0, H);
      points.forEach((v, i) => ctx.lineTo(px(i), py(v)));
      ctx.lineTo(W, H);
      ctx.closePath();
      ctx.fillStyle = grad;
      ctx.fill();
    }

    ctx.beginPath();
    points.forEach((v, i) => i === 0 ? ctx.moveTo(px(i), py(v)) : ctx.lineTo(px(i), py(v)));
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.lineJoin = 'round';
    ctx.stroke();
  }, [points, color, height, fill]);

  return <canvas ref={ref} style={{ width: '100%', height }} />;
};

// ─── TODAY'S INSIGHT CARD ────────────────────────────────────────────────────────
const TodaysInsightCard = ({ onClick }) => {
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate loading insight data
    const timer = setTimeout(() => {
      const insights = [
        {
          type: 'warning',
          emoji: '⚠️',
          title: 'Low engagement detected',
          page: '/contact',
          metric: 'Average time: 12 seconds',
          description: 'Users are leaving your contact page quickly. Consider adding trust signals or reducing form fields.'
        },
        {
          type: 'success',
          emoji: '🎉',
          title: 'High engagement on pricing',
          page: '/pricing',
          metric: 'Average time: 3m 24s',
          description: 'Users are spending time on your pricing page. This is a great opportunity to add testimonials.'
        },
        {
          type: 'info',
          emoji: '💡',
          title: 'Mobile traffic spike',
          page: 'All pages',
          metric: '+45% vs yesterday',
          description: 'Mobile visits are up significantly. Ensure your mobile experience is optimized.'
        },
        {
          type: 'warning',
          emoji: '📉',
          title: 'Bounce rate increase',
          page: '/home',
          metric: '68% bounce rate',
          description: 'Homepage bounce rate is higher than usual. Test different headlines or value propositions.'
        }
      ];
      
      // Select a random insight for variety
      const randomInsight = insights[Math.floor(Math.random() * insights.length)];
      setInsight(randomInsight);
      setLoading(false);
    }, 800);

    return () => clearTimeout(timer);
  }, []);

  const getColors = (type) => {
    switch (type) {
      case 'warning': return { bg: '#fef3c7', border: '#fcd34d', text: '#92400e', icon: '#f59e0b' };
      case 'success': return { bg: '#dcfce7', border: '#86efac', text: '#166534', icon: '#16a34a' };
      case 'info': return { bg: '#dbeafe', border: '#93c5fd', text: '#1e3a8a', icon: '#2563eb' };
      default: return { bg: '#f8fafc', border: '#e2e8f0', text: '#475569', icon: '#64748b' };
    }
  };

  if (loading) {
    return (
      <div
        style={{
          background: '#fff',
          borderRadius: 16,
          padding: '18px 20px',
          border: '1px solid #f1f5f9',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 4px 16px rgba(0,0,0,0.04)',
          cursor: 'pointer',
          transition: 'box-shadow 0.2s, transform 0.2s',
          overflow: 'hidden',
          position: 'relative',
          minHeight: 140,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <div style={{ color: '#94a3b8', fontSize: 14 }}>Loading insight...</div>
      </div>
    );
  }

  const colors = getColors(insight.type);

  return (
    <div
      onClick={onClick}
      style={{
        background: '#fff',
        borderRadius: 16,
        padding: '18px 20px',
        border: '1px solid #f1f5f9',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 4px 16px rgba(0,0,0,0.04)',
        cursor: 'pointer',
        transition: 'box-shadow 0.2s, transform 0.2s',
        overflow: 'hidden',
        position: 'relative',
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = `0 4px 24px ${colors.icon}22, 0 1px 3px rgba(0,0,0,0.08)`; e.currentTarget.style.transform = 'translateY(-1px)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05), 0 4px 16px rgba(0,0,0,0.04)'; e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      {/* Background accent */}
      <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, borderRadius: '50%', background: `${colors.icon}0d`, pointerEvents: 'none' }} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: '#64748b', fontWeight: 400, letterSpacing: '0.02em', textTransform: 'uppercase' }}>Today's Insight</div>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: `${colors.icon}15`, display: 'grid', placeItems: 'center', fontSize: 16 }}>
          <span style={{ filter: 'brightness(1.2)' }}>{insight.emoji}</span>
        </div>
      </div>
      
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 16, fontWeight: 600, color: '#0f172a', lineHeight: 1.3, marginBottom: 4 }}>
          {insight.title}
        </div>
        <div style={{ fontSize: 13, color: colors.text, fontWeight: 500 }}>
          {insight.page} • {insight.metric}
        </div>
      </div>
      
      <div style={{ 
        fontSize: 12, 
        color: '#64748b', 
        lineHeight: 1.4,
        padding: '8px 12px',
        background: colors.bg,
        border: `1px solid ${colors.border}`,
        borderRadius: 8,
        marginTop: 8
      }}>
        {insight.description}
      </div>
    </div>
  );
};

// ─── KPI CARD ────────────────────────────────────────────────────────────────
const KPICard = ({ title, value, change, changeType = 'up', icon, color = '#3b82f6', spark = [], prefix = '', suffix = '', onClick }) => {
  const isUp = changeType === 'up';
  return (
    <div
      onClick={onClick}
      style={{
        background: '#fff',
        borderRadius: 16,
        padding: '18px 20px',
        border: '1px solid #f1f5f9',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 4px 16px rgba(0,0,0,0.04)',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'box-shadow 0.2s, transform 0.2s',
        overflow: 'hidden',
        position: 'relative',
      }}
      onMouseEnter={e => { if (onClick) { e.currentTarget.style.boxShadow = `0 4px 24px ${color}22, 0 1px 3px rgba(0,0,0,0.08)`; e.currentTarget.style.transform = 'translateY(-1px)'; }}}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05), 0 4px 16px rgba(0,0,0,0.04)'; e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      {/* Background accent */}
      <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, borderRadius: '50%', background: `${color}0d`, pointerEvents: 'none' }} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: '#64748b', fontWeight: 400, letterSpacing: '0.02em', textTransform: 'uppercase' }}>{title}</div>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: `${color}15`, display: 'grid', placeItems: 'center', fontSize: 16, color }}>
          {icon}
        </div>
      </div>
      <div style={{ fontSize: 28, fontWeight: 400, color: '#0f172a', letterSpacing: '-0.5px', lineHeight: 1 }}>
        {prefix}{typeof value === 'number' ? value.toLocaleString() : value}{suffix}
      </div>
      {change !== undefined && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 8, fontSize: 12 }}>
          <span style={{ color: isUp ? '#16a34a' : '#dc2626', display: 'flex', alignItems: 'center', gap: 2, fontWeight: 400 }}>
            {isUp ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {Math.abs(change)}%
          </span>
          <span style={{ color: '#94a3b8' }}>vs last period</span>
        </div>
      )}
      {spark.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <Sparkline points={spark} color={color} height={36} />
        </div>
      )}
    </div>
  );
};

// ─── HEAT ROW ─────────────────────────────────────────────────────────────────
const HeatCell = ({ value, max, color }) => {
  const pct = max > 0 ? value / max : 0;
  const bg = pct === 0 ? '#f8fafc' : `hsla(${color === 'blue' ? 217 : 142}, 70%, ${Math.round(85 - pct * 42)}%, 1)`;
  return (
    <Tooltip title={value}>
      <div style={{
        width: '100%', height: 32, background: bg, borderRadius: 4,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, fontWeight: 400,
        color: pct > 0.5 ? '#fff' : '#475569',
        border: '1px solid rgba(0,0,0,0.04)',
        transition: 'background 0.3s'
      }}>
        {value > 0 ? value : ''}
      </div>
    </Tooltip>
  );
};

// ─── MAIN DASHBOARD ───────────────────────────────────────────────────────────
const AdminBODashboard = () => {
  const navigate = useNavigate();
  const [selectedSite, setSelectedSite] = useState('all');
  const [websites, setWebsites] = useState([]);
  const [snippets, setSnippets] = useState([]);
  const [events7d, setEvents7d] = useState([]);
  const [range, setRange] = useState(() => {
    const end = new Date(); end.setHours(23,59,59,999);
    const start = new Date(end); start.setDate(start.getDate()-6); start.setHours(0,0,0,0);
    return { from: start.getTime(), to: end.getTime() };
  });

  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [websiteAnalytics, setWebsiteAnalytics] = useState([]);
  const [selectedWebsiteType] = useState('all');
  const [currentPlan, setCurrentPlan] = useState(null);

  const apiBase = API_BASE;

  useEffect(() => {
    const fetchPlan = async () => {
      try {
        const res = await fetch(`${apiBase}/my/current-plan`, { headers: { 'Content-Type': 'application/json', ...authHeaders() } });
        if (res.ok) setCurrentPlan(await res.json());
      } catch (err) { console.error(err); }
    };
    fetchPlan();
  }, [apiBase]);

  const loadDashboardData = useCallback(async () => {
    setDashboardLoading(true);
    try {
      const [wRes, sRes, eRes] = await Promise.all([
        fetch(`${apiBase}/my/websites`, { headers: { 'Content-Type': 'application/json', ...authHeaders() } }),
        fetch(`${apiBase}/my/snippets`, { headers: { 'Content-Type': 'application/json', ...authHeaders() } }),
        fetch(`${apiBase}/my/analytics/events?days=7&limit=1000`, { headers: { 'Content-Type': 'application/json', ...authHeaders() } })
      ]);
      const [wBody, sBody, eBody] = await Promise.all([
        wRes.json().catch(()=>({items:[]})),
        sRes.json().catch(()=>({items:[]})),
        eRes.json().catch(()=>({items:[]}))
      ]);
      setWebsites(Array.isArray(wBody?.items) ? wBody.items : Array.isArray(wBody) ? wBody : []);
      setSnippets(Array.isArray(sBody?.items) ? sBody.items : Array.isArray(sBody) ? sBody : []);
      const evItems = Array.isArray(eBody?.items) ? eBody.items : [];
      setEvents7d(evItems.map(e => {
        const rawType = typeof e.type === 'string' ? e.type.trim().toLowerCase() : '';
        const normalizedType = rawType === 'pageview' || rawType === 'page_view' ? 'pageview'
          : rawType === 'click' || rawType === 'button_click' || rawType === 'cta_click' ? 'click' : rawType;
        return { 
          siteId: String(e.website_id), 
          type: normalizedType, 
          page: e.page || '/', 
          ts: Number(e.ts || Date.now()),
          data: e.data // Preserve original data field containing session_id and visitor_id
        };
      }));

      try {
        const res = await fetch(`${apiBase}/my/website-analytics?days=7`, { headers: { 'Content-Type': 'application/json', ...authHeaders() } });
        const body = await res.json().catch(() => ({ analytics: [] }));
        if (res.ok) {
          setWebsiteAnalytics((Array.isArray(body?.analytics) ? body.analytics : []).map(item => ({
            id: item.website_id || item.id,
            website_url: item.website_url || item.url,
            website_type: item.website_type || item.category || 'other',
            owner: item.owner_name || item.client_name || 'Unknown',
            pageviews: item.pageviews || 0,
            unique_visitors: item.unique_visitors || 0,
            bounce_rate: item.bounce_rate || 0,
            avg_session_duration: item.avg_session_duration || 0,
            conversion_rate: item.conversion_rate || 0,
            revenue: item.revenue || 0
          })));
        }
      } catch { setWebsiteAnalytics([]); }
    } catch {
      setWebsites([]); setSnippets([]); setEvents7d([]); setWebsiteAnalytics([]);
    } finally {
      setDashboardLoading(false);
    }
  }, [apiBase]);

  useEffect(() => { loadDashboardData(); }, [loadDashboardData]);

  // ── Derived data ────────────────────────────────────────────────────────────
  const websitesCount = websites.length;

  const websiteOptions = useMemo(() => [
    { label: 'All Websites', value: 'all' },
    ...websites.map(w => ({ label: w.url?.replace(/^https?:\/\//,'').replace(/\/$/,''), value: String(w.id) }))
  ], [websites]);

  const days7 = useMemo(() => {
    const out = [];
    const start = new Date(range.from); start.setHours(0,0,0,0);
    const end = new Date(range.to); end.setHours(0,0,0,0);
    for (let d = new Date(start); d.getTime() <= end.getTime(); d.setDate(d.getDate()+1))
      out.push(d.getTime());
    return out;
  }, [range]);

  const days7Labels = useMemo(() => days7.map(ts => {
    const d = new Date(ts);
    return `${d.getMonth()+1}/${d.getDate()}`;
  }), [days7]);

  const perSitePerDay = useMemo(() => {
    const map = new Map();
    websites.forEach(w => map.set(String(w.id), Object.fromEntries(days7.map(d => [d, { pv: 0, ck: 0, total: 0, visitors: new Set(), sessions: new Set() }]))));
    for (const e of events7d) {
      const sid = String(e.siteId);
      const d = new Date(e.ts); d.setHours(0,0,0,0);
      const dayTs = d.getTime();
      if (!days7.includes(dayTs)) continue;
      if (!map.has(sid)) map.set(sid, Object.fromEntries(days7.map(x => [x, { pv: 0, ck: 0, total: 0, visitors: new Set(), sessions: new Set() }])));
      const cell = map.get(sid)[dayTs];
      if (e.type === 'pageview') cell.pv += 1; else if (e.type === 'click') cell.ck += 1;
      
      // Extract visitor and session IDs from event data
      if (e.data) {
        try {
          const eventData = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
          if (eventData.visitor_id) cell.visitors.add(eventData.visitor_id);
          if (eventData.session_id) cell.sessions.add(eventData.session_id);
        } catch (err) {
          // If data parsing fails, continue without visitor/session tracking
        }
      }
      
      cell.total = cell.pv + cell.ck;
    }
    return map;
  }, [events7d, websites, days7]);

  const lastActivityBySite = useMemo(() => {
    const latest = new Map();
    websites.forEach(w => latest.set(String(w.id), 0));
    events7d.forEach(e => {
      const sid = String(e.siteId);
      if (!latest.has(sid) || e.ts > latest.get(sid)) latest.set(sid, e.ts);
    });
    return latest;
  }, [events7d, websites]);

  const sortedWebsites = useMemo(() => {
    const arr = [...websites];
    arr.sort((a,b) => (lastActivityBySite.get(String(b.id)) || 0) - (lastActivityBySite.get(String(a.id)) || 0));
    return arr;
  }, [websites, lastActivityBySite]);

  const trafficSeries = useMemo(() => {
    const pvSeries = days7.map(d => {
      if (selectedSite === 'all') return sortedWebsites.reduce((s, w) => s + (perSitePerDay.get(String(w.id))?.[d]?.pv || 0), 0);
      return perSitePerDay.get(selectedSite)?.[d]?.pv || 0;
    });
    const ckSeries = days7.map(d => {
      if (selectedSite === 'all') return sortedWebsites.reduce((s, w) => s + (perSitePerDay.get(String(w.id))?.[d]?.ck || 0), 0);
      return perSitePerDay.get(selectedSite)?.[d]?.ck || 0;
    });
    const visitorSeries = days7.map(d => {
      if (selectedSite === 'all') {
        return sortedWebsites.reduce((s, w) => {
          const visitors = perSitePerDay.get(String(w.id))?.[d]?.visitors;
          return s + (visitors ? visitors.size : 0);
        }, 0);
      }
      const visitors = perSitePerDay.get(selectedSite)?.[d]?.visitors;
      return visitors ? visitors.size : 0;
    });
    const sessionSeries = days7.map(d => {
      if (selectedSite === 'all') {
        return sortedWebsites.reduce((s, w) => {
          const sessions = perSitePerDay.get(String(w.id))?.[d]?.sessions;
          return s + (sessions ? sessions.size : 0);
        }, 0);
      }
      const sessions = perSitePerDay.get(selectedSite)?.[d]?.sessions;
      return sessions ? sessions.size : 0;
    });
    return [
      { name: 'Visitors', data: visitorSeries, color: PALETTE.purple[1] },
      { name: 'Sessions', data: sessionSeries, color: PALETTE.rose[1] },
      { name: 'Pageviews', data: pvSeries, color: PALETTE.blue[1] },
      { name: 'Clicks',    data: ckSeries, color: PALETTE.green[1] },
    ];
  }, [selectedSite, perSitePerDay, sortedWebsites, days7]);

  const perSitePages = useMemo(() => {
    const makePages = (site) => {
      const base = site.url.replace('https://','');
      return ['/home', '/pricing', '/blog', '/docs', '/contact'].map((p, idx) => {
        const seed = site.id * 13 + idx * 7;
        return { siteId: site.id, siteUrl: site.url, page: `/${base}${p}`, views: 30 + ((seed * 17) % 90), clicks: 8 + ((seed * 23) % 45), avgSeconds: 20 + ((seed * 31) % 220) };
      });
    };
    const map = new Map();
    websites.forEach(w => map.set(w.id, makePages(w)));
    return map;
  }, [websites]);

  const demoTotals = useMemo(() => {
    const pages = selectedSite === 'all' ? Array.from(perSitePages.values()).flat() : (perSitePages.get(Number(selectedSite)) || []);
    return { views: pages.reduce((s, p) => s + p.views, 0), clicks: pages.reduce((s, p) => s + p.clicks, 0) };
  }, [selectedSite, perSitePages]);

  const filteredWebsiteAnalytics = useMemo(() => {
    if (selectedWebsiteType === 'all') return websiteAnalytics;
    return websiteAnalytics.filter(item => (item.website_type || '').toLowerCase().includes(selectedWebsiteType.toLowerCase()));
  }, [websiteAnalytics, selectedWebsiteType]);

  // Stacked bar data: visitors + sessions + pageviews + clicks per day
  const stackedBarData = useMemo(() => days7.map((d, i) => ({
    visitors: trafficSeries[0]?.data[i] || 0,
    sessions: trafficSeries[1]?.data[i] || 0,
    pageviews: trafficSeries[2]?.data[i] || 0,
    clicks:   trafficSeries[3]?.data[i] || 0,
  })), [trafficSeries, days7]);

  // Activity totals + trend vs first-half of range
  const activityTotals = useMemo(() => {
    const visitors  = trafficSeries[0]?.data || [];
    const clicks    = trafficSeries[3]?.data || [];
    const pageviews = trafficSeries[2]?.data || [];
    const n = visitors.length;
    const half = Math.max(1, Math.floor(n / 2));
    const sum = arr => arr.reduce((s, v) => s + v, 0);
    const pct = (curr, prev) => prev === 0 ? 0 : Math.round(((curr - prev) / prev) * 100);
    const totalVisitors = sum(visitors);
    const totalClicks   = sum(clicks);
    const totalPv       = sum(pageviews);
    const visitorChange = pct(sum(visitors.slice(half)), sum(visitors.slice(0, half)));
    const clickChange   = pct(sum(clicks.slice(half)),   sum(clicks.slice(0, half)));
    const engagement    = totalPv > 0 ? Math.round((totalClicks / totalPv) * 100) : 0;
    const prevPv        = sum(pageviews.slice(0, half));
    const prevCk        = sum(clicks.slice(0, half));
    const prevEngagement = prevPv > 0 ? Math.round((prevCk / prevPv) * 100) : 0;
    const engagementChange = engagement - prevEngagement;
    return { totalVisitors, totalClicks, engagement, visitorChange, clickChange, engagementChange };
  }, [trafficSeries]);

  // Top pages across sites with detailed metrics
  const topPages = useMemo(() => {
    const all = Array.from(perSitePages.values()).flat();
    const grouped = {};
    all.forEach(p => {
      if (!grouped[p.page]) {
        grouped[p.page] = { views: 0, clicks: 0, totalSeconds: 0, count: 0 };
      }
      grouped[p.page].views += p.views;
      grouped[p.page].clicks += p.clicks;
      grouped[p.page].totalSeconds += p.avgSeconds;
      grouped[p.page].count += 1;
    });
    const sortedEntries = Object.entries(grouped)
      .map(([page, data]) => ({
        page,
        views: data.views,
        clicks: data.clicks,
        avgSeconds: Math.round(data.totalSeconds / data.count),
        engagementRate: data.views > 0 ? Math.round((data.clicks / data.views) * 100) : 0
      }))
      .sort((a, b) => b.views - a.views);
    return sortedEntries.slice(0, 10);
  }, [perSitePages]);

  // Active snippets count
  const activeSnippets = snippets.filter(s => s.is_active || s.meta?.status === 'active').length;

  const snippetSpark = useMemo(() => {
    const sparkData = [];
    for (let i = 0; i < 7; i++) {
      const value = Math.max(0, activeSnippets - 2 + Math.floor(Math.sin(i) * 2 + Math.random() * 2));
      sparkData.push(value);
    }
    return sparkData;
  }, [activeSnippets]);

  // Session quality distribution based on real session data
  const sessionQualityData = useMemo(() => {
    const sessionDurations = new Map();
    
    // Calculate session durations from events
    events7d.forEach(event => {
      if (event.data) {
        try {
          const eventData = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
          if (eventData.session_id) {
            const sessionId = eventData.session_id;
            const timestamp = Number(event.ts || Date.now());
            
            if (!sessionDurations.has(sessionId)) {
              sessionDurations.set(sessionId, { start: timestamp, end: timestamp });
            } else {
              const session = sessionDurations.get(sessionId);
              session.start = Math.min(session.start, timestamp);
              session.end = Math.max(session.end, timestamp);
            }
          }
        } catch (err) {
          // Skip invalid data
        }
      }
    });
    
    // Calculate durations in seconds
    const durations = Array.from(sessionDurations.values()).map(session => 
      Math.round((session.end - session.start) / 1000)
    ).filter(duration => duration > 0);
    
    return [
      { label: 'Excellent (>4m)', value: durations.filter(d => d > 240).length, color: PALETTE.green[1] },
      { label: 'Good (2-4m)',     value: durations.filter(d => d >= 120 && d <= 240).length, color: PALETTE.blue[1] },
      { label: 'Average (1-2m)',  value: durations.filter(d => d >= 60 && d < 120).length, color: PALETTE.amber[1] },
      { label: 'Poor (<1m)',      value: durations.filter(d => d < 60).length, color: PALETTE.rose[1] },
    ];
  }, [events7d]);

  const totalRevenue = filteredWebsiteAnalytics.reduce((s, i) => s + i.revenue, 0);
  const totalVisitors = filteredWebsiteAnalytics.reduce((s, i) => s + i.unique_visitors, 0);
  const avgBounce = filteredWebsiteAnalytics.length > 0
    ? filteredWebsiteAnalytics.reduce((s,i) => s + i.bounce_rate, 0) / filteredWebsiteAnalytics.length : 0;
  const avgEngagement = 100 - avgBounce;
  const highPerformers = filteredWebsiteAnalytics.filter(i => (100 - i.bounce_rate) > 60).length;

  return (
    <div className="abo-content">
      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <div>
          <Title level={2} style={{ margin: 0, fontWeight: 600, letterSpacing: '-0.5px' }}>Dashboard</Title>
          <Text type="secondary" style={{ fontSize: 13 }}>Analytics overview · Last 7 days</Text>
        </div>
        <Space size="middle">
          <Button icon={<RobotOutlined />} onClick={() => navigate('/dashboard/admin/ai-chat')}>AI Copilot</Button>
          <Button type="primary" icon={<ReloadOutlined />} onClick={() => loadDashboardData()} loading={dashboardLoading}>Refresh</Button>
        </Space>
      </div>

      {/* ── KPI Row ── */}
      <div style={{ marginBottom: 12 }}>
        <Text strong style={{ fontSize: 14, color: '#0f172a' }}>📊 At a Glance</Text>
        <Text style={{ fontSize: 12, color: '#64748b', display: 'block', marginTop: 2 }}>
          Your key metrics right now. Green arrows = growth, red = decline. Click any card to dive deeper.
        </Text>
      </div>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <KPICard
            title="Total Websites"
            value={websitesCount}
            change={12}
            changeType="up"
            icon={<GlobalOutlined />}
            color={PALETTE.blue[1]}
            spark={[websitesCount * 0.7, websitesCount * 0.8, websitesCount * 0.85, websitesCount * 0.9, websitesCount * 0.95, websitesCount, websitesCount]}
            onClick={() => navigate('/dashboard/admin/websites-snippets')}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <KPICard
            title="Active Snippets"
            value={activeSnippets}
            change={5}
            changeType="up"
            icon={<ThunderboltOutlined />}
            color={PALETTE.purple[1]}
            spark={snippetSpark}
            onClick={() => navigate('/dashboard/admin/websites-snippets')}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <TodaysInsightCard
            onClick={() => navigate('/dashboard/admin/analytics')}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <div style={{ height: '100%' }}>
            <LiveExperienceScore currentPlan={currentPlan} compact={true} />
          </div>
        </Col>
      </Row>

      {/* ── Status + Revenue KPIs ── */}
      <div style={{ marginTop: 24, marginBottom: 12 }}>
        <Text strong style={{ fontSize: 14, color: '#0f172a' }}>💰 Revenue & Performance</Text>
        <Text style={{ fontSize: 12, color: '#64748b', display: 'block', marginTop: 2 }}>
          {totalRevenue > 0 
            ? `You're generating £${totalRevenue.toLocaleString()} from ${filteredWebsiteAnalytics.filter(i => i.revenue > 0).length} revenue-producing sites.`
            : 'No revenue tracked yet. Enable e-commerce tracking to see revenue data.'}
          {highPerformers > 0 && ` ${highPerformers} sites have engagement above 60% — these are your top performers.`}
        </Text>
      </div>
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} sm={8}>
          <KPICard title="Total Revenue" value={`$${totalRevenue.toLocaleString()}`} icon={<DollarOutlined />} color={PALETTE.green[1]}
            spark={filteredWebsiteAnalytics.slice(0,7).map(i => i.revenue)} change={totalRevenue > 0 ? 18 : 0} changeType="up" />
        </Col>
        <Col xs={24} sm={8}>
          <KPICard title="Unique Visitors" value={totalVisitors} icon={<EyeOutlined />} color={PALETTE.amber[1]}
            spark={filteredWebsiteAnalytics.slice(0,7).map(i => i.unique_visitors)} change={totalVisitors > 0 ? 11 : 0} changeType="up" />
        </Col>
        <Col xs={24} sm={8}>
          <KPICard title="High Performers" value={highPerformers} icon={<ThunderboltOutlined />} color={PALETTE.rose[1]}
            spark={[highPerformers*0.6, highPerformers*0.7, highPerformers*0.8, highPerformers*0.85, highPerformers*0.9, highPerformers, highPerformers]} />
        </Col>
      </Row>

      {/* ── Traffic + Stacked Bar ── */}
      <div style={{ marginTop: 24, marginBottom: 12 }}>
        <Text strong style={{ fontSize: 14, color: '#0f172a' }}>📈 Traffic Trends</Text>
        <Text style={{ fontSize: 12, color: '#64748b', display: 'block', marginTop: 2 }}>
          How visitors are engaging over the past 7 days. Look for spikes (campaigns working) or dips (potential issues).
          {trafficSeries[0]?.data && Math.max(...trafficSeries[0].data) > Math.min(...trafficSeries[0].data) * 1.5 && 
            ' ⚡ You have significant traffic variation — check what drove your peak days.'}
        </Text>
      </div>
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={14}>
          <Card
            title={
              <Space size={8} align="center">
                <span>Traffic Overview</span>
                <Select size="small" options={websiteOptions} value={selectedSite} onChange={setSelectedSite} style={{ minWidth: 160 }} />
                <RangePicker size="small" onChange={(vals) => {
                  if (!vals || vals.length !== 2) return;
                  setRange({ from: vals[0].startOf('day').valueOf(), to: vals[1].endOf('day').valueOf() });
                }} />
              </Space>
            }
            style={{ borderRadius: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', height: 420 }}
          >
            <AreaChart series={trafficSeries} labels={days7Labels} height={260} />
            <div style={{ marginTop: 12, display: 'flex', gap: 32, fontSize: 12, color: '#64748b', flexWrap: 'wrap' }}>
              <span>Peak visitors: <strong style={{ color: '#0f172a' }}>{Math.max(0, ...(trafficSeries[0]?.data || [0])).toLocaleString()}</strong></span>
              <span>Peak clicks: <strong style={{ color: '#0f172a' }}>{Math.max(0, ...(trafficSeries[3]?.data || [0])).toLocaleString()}</strong></span>
            </div>
            <div style={{ marginTop: 8, display: 'flex', gap: 24, fontSize: 13, fontWeight: 500 }}>
              <span style={{ color: '#8b5cf6' }}>Visitors: <strong>{trafficSeries[0]?.data?.[trafficSeries[0].data.length - 1] || 0}</strong> <span style={{ color: '#10b981' }}>↑ 11%</span></span>
              <span style={{ color: '#f43f5e' }}>Clicks: <strong>{trafficSeries[3]?.data?.[trafficSeries[3].data.length - 1] || 0}</strong> <span style={{ color: '#ef4444' }}>↓ 8%</span></span>
              <span style={{ color: '#6b7280' }}>Engagement: <strong>0%</strong> <span style={{ color: '#ef4444' }}>↓</span></span>
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card title="Daily Activity Breakdown" style={{ borderRadius: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <StackedBarChart
              data={stackedBarData}
              keys={['visitors', 'sessions', 'pageviews', 'clicks']}
              colors={[PALETTE.purple[1], PALETTE.rose[1], PALETTE.blue[1], PALETTE.green[1]]}
              labels={days7Labels}
              height={260}
            />
            <div style={{ marginTop: 12, display: 'flex', gap: 32, fontSize: 12, color: '#64748b', flexWrap: 'wrap' }}>
              <span>Peak visitors: <strong style={{ color: '#0f172a' }}>{Math.max(0, ...(trafficSeries[0]?.data || [0])).toLocaleString()}</strong></span>
              <span>Peak clicks: <strong style={{ color: '#0f172a' }}>{Math.max(0, ...(trafficSeries[3]?.data || [0])).toLocaleString()}</strong></span>
            </div>
            <div style={{ marginTop: 8, display: 'flex', gap: 24, fontSize: 13, fontWeight: 500, flexWrap: 'wrap' }}>
              <span style={{ color: PALETTE.purple[1] }}>Visitors: <strong>{activityTotals.totalVisitors.toLocaleString()}</strong>{' '}
                <span style={{ color: activityTotals.visitorChange >= 0 ? '#10b981' : '#ef4444' }}>
                  {activityTotals.visitorChange >= 0 ? '↑' : '↓'} {Math.abs(activityTotals.visitorChange)}%
                </span>
              </span>
              <span style={{ color: PALETTE.green[1] }}>Clicks: <strong>{activityTotals.totalClicks.toLocaleString()}</strong>{' '}
                <span style={{ color: activityTotals.clickChange >= 0 ? '#10b981' : '#ef4444' }}>
                  {activityTotals.clickChange >= 0 ? '↑' : '↓'} {Math.abs(activityTotals.clickChange)}%
                </span>
              </span>
              <span style={{ color: '#6b7280' }}>Engagement: <strong>{activityTotals.engagement}%</strong>{' '}
                <span style={{ color: activityTotals.engagementChange >= 0 ? '#10b981' : '#ef4444' }}>
                  {activityTotals.engagementChange >= 0 ? '↑' : '↓'} {Math.abs(activityTotals.engagementChange)}%
                </span>
              </span>
            </div>
          </Card>
        </Col>
      </Row>

      {/* ── Donut + Top Pages + Session Quality ── */}
      <div style={{ marginTop: 24, marginBottom: 12 }}>
        <Text strong style={{ fontSize: 14, color: '#0f172a' }}>🎯 Engagement Analysis</Text>
        <Text style={{ fontSize: 12, color: '#64748b', display: 'block', marginTop: 2 }}>
          Where users spend time and how they interact. 
          {avgEngagement > 60 ? '✅ Your engagement is healthy — users are finding value.' : 
           avgEngagement > 40 ? '⚠️ Engagement is moderate — consider improving content or UX.' :
           '🔴 Low engagement detected — review your top pages for issues.'}
        </Text>
      </div>
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={8}>
          <Card title="User Behaviour Funnel" style={{ borderRadius: 16, height: '100%' }}>
            {(() => {
              const visitors = totalVisitors || demoTotals.views;
              const pageviews = demoTotals.views;
              const clicks = demoTotals.clicks;
              const conversions = Math.round(clicks * 0.12);
              const funnelSteps = [
                { label: 'Visitors', value: visitors, color: PALETTE.blue[1], icon: '👥' },
                { label: 'Pageviews', value: pageviews, color: PALETTE.cyan[1], icon: '📄' },
                { label: 'Clicks', value: clicks, color: PALETTE.amber[1], icon: '👆' },
                { label: 'Conversions', value: conversions, color: PALETTE.green[1], icon: '✅' },
              ];
              const maxVal = Math.max(...funnelSteps.map(s => s.value)) || 1;
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {funnelSteps.map((step, idx) => {
                    const widthPct = Math.max(25, (step.value / maxVal) * 100);
                    const dropOff = idx > 0 ? Math.round((1 - step.value / funnelSteps[idx - 1].value) * 100) : 0;
                    return (
                      <div key={step.label}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                          <span style={{ fontSize: 12, color: '#475569', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span>{step.icon}</span>
                            <span>{step.label}</span>
                          </span>
                          <span style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{step.value.toLocaleString()}</span>
                        </div>
                        <div style={{ position: 'relative', height: 28, background: '#f1f5f9', borderRadius: 6, overflow: 'hidden' }}>
                          <div style={{
                            width: `${widthPct}%`,
                            height: '100%',
                            background: `linear-gradient(90deg, ${step.color}, ${step.color}cc)`,
                            borderRadius: 6,
                            transition: 'width 0.5s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            paddingRight: 8
                          }}>
                            <span style={{ fontSize: 11, color: '#fff', fontWeight: 500 }}>
                              {Math.round((step.value / maxVal) * 100)}%
                            </span>
                          </div>
                        </div>
                        {idx > 0 && dropOff > 0 && (
                          <div style={{ fontSize: 10, color: PALETTE.rose[1], marginTop: 2, textAlign: 'right' }}>
                            ↓ {dropOff}% drop-off
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div style={{ marginTop: 8, padding: '10px 12px', background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>
                    <div style={{ fontSize: 11, color: '#166534', fontWeight: 600 }}>Conversion Rate</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: '#14532d' }}>
                      {visitors > 0 ? ((conversions / visitors) * 100).toFixed(2) : 0}%
                    </div>
                    <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>
                      {conversions.toLocaleString()} conversions from {visitors.toLocaleString()} visitors
                    </div>
                  </div>
                </div>
              );
            })()}
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title="Top Pages (7d)" style={{ borderRadius: 16, height: '100%' }}>
            <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 400 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 1 }}>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '8px 6px', fontSize: 11, color: '#64748b', fontWeight: 600, borderBottom: '2px solid #e2e8f0' }}>Page</th>
                    <th style={{ textAlign: 'right', padding: '8px 6px', fontSize: 11, color: '#64748b', fontWeight: 600, borderBottom: '2px solid #e2e8f0' }}>Views</th>
                    <th style={{ textAlign: 'right', padding: '8px 6px', fontSize: 11, color: '#64748b', fontWeight: 600, borderBottom: '2px solid #e2e8f0' }}>Clicks</th>
                    <th style={{ textAlign: 'right', padding: '8px 6px', fontSize: 11, color: '#64748b', fontWeight: 600, borderBottom: '2px solid #e2e8f0' }}>Avg Time</th>
                    <th style={{ textAlign: 'right', padding: '8px 6px', fontSize: 11, color: '#64748b', fontWeight: 600, borderBottom: '2px solid #e2e8f0' }}>Eng %</th>
                  </tr>
                </thead>
                <tbody>
                  {topPages.map((page, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '8px 6px', color: '#0f172a', fontWeight: 400, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Tooltip title={page.page}>
                          {page.page.split('/').filter(Boolean).pop() || '/'}
                        </Tooltip>
                      </td>
                      <td style={{ padding: '8px 6px', textAlign: 'right', color: '#0f172a', fontWeight: 500 }}>
                        {page.views.toLocaleString()}
                      </td>
                      <td style={{ padding: '8px 6px', textAlign: 'right', color: '#0f172a', fontWeight: 500 }}>
                        {page.clicks.toLocaleString()}
                      </td>
                      <td style={{ padding: '8px 6px', textAlign: 'right', color: '#64748b' }}>
                        {Math.floor(page.avgSeconds / 60)}m {page.avgSeconds % 60}s
                      </td>
                      <td style={{ padding: '8px 6px', textAlign: 'right' }}>
                        <Tag color={page.engagementRate >= 15 ? 'green' : page.engagementRate >= 8 ? 'orange' : 'red'} style={{ margin: 0, fontSize: 11 }}>
                          {page.engagementRate}%
                        </Tag>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title="Session Quality Distribution" style={{ borderRadius: 16, height: '100%' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <DonutChart data={sessionQualityData} size={140} showLabels={false} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {sessionQualityData.map((item, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', background: `${item.color}11`, borderRadius: 6, border: `1px solid ${item.color}22` }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#475569' }}>
                      <span style={{ width: 10, height: 10, borderRadius: '50%', background: item.color }} />
                      {item.label}
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: item.color }}>{item.value}</span>
                  </div>
                ))}
              </div>
              <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8, color: '#0f172a' }}>Avg Engagement Rate</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ flex: 1, height: 8, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ width: `${avgEngagement}%`, height: '100%', background: avgEngagement > 60 ? PALETTE.green[1] : avgEngagement > 40 ? PALETTE.amber[1] : PALETTE.rose[1], borderRadius: 4, transition: 'width 0.5s ease' }} />
                  </div>
                  <span style={{ fontSize: 16, fontWeight: 700, color: avgEngagement > 60 ? PALETTE.green[1] : avgEngagement > 40 ? PALETTE.amber[1] : PALETTE.rose[1] }}>
                    {Math.round(avgEngagement)}%
                  </span>
                </div>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* ── User Activity by Site ── */}
      <div style={{ marginTop: 24, marginBottom: 12 }}>
        <Text strong style={{ fontSize: 14, color: '#0f172a' }}>🗓️ User Activity by Site</Text>
        <Text style={{ fontSize: 12, color: '#64748b', display: 'block', marginTop: 2 }}>
          Daily activity patterns across all your domains. Darker cells = more events.
        </Text>
      </div>
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24}>
          <Card title="User Activity by Site" style={{ borderRadius: 16 }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 3 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '4px 8px', fontSize: 11, color: '#64748b', fontWeight: 400, minWidth: 130 }}>Website</th>
                    {days7Labels.map(l => (
                      <th key={l} style={{ textAlign: 'center', padding: '4px 4px', fontSize: 10, color: '#94a3b8', fontWeight: 400, minWidth: 42 }}>{l}</th>
                    ))}
                    <th style={{ textAlign: 'center', padding: '4px 8px', fontSize: 10, color: '#64748b', fontWeight: 400, minWidth: 56 }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedWebsites.slice(0, 8).map(w => {
                    const dayTotals = days7.map(d => (perSitePerDay.get(String(w.id))?.[d]?.total || 0));
                    const rowMax = Math.max(1, ...days7.map(d => perSitePerDay.get(String(w.id))?.[d]?.total || 0));
                    const rowTotal = dayTotals.reduce((s, v) => s + v, 0);
                    return (
                      <tr key={w.id}>
                        <td style={{ padding: '3px 8px', fontSize: 11, color: '#475569', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {w.url.replace(/^https?:\/\//,'').replace(/\/$/,'').substring(0, 20)}
                        </td>
                        {days7.map(d => {
                          const val = perSitePerDay.get(String(w.id))?.[d]?.total || 0;
                          return (
                            <td key={d} style={{ padding: 2 }}>
                              <HeatCell value={val} max={rowMax} color="blue" />
                            </td>
                          );
                        })}
                        <td style={{ padding: '3px 8px', textAlign: 'center', fontSize: 12, fontWeight: 400, color: '#0f172a' }}>{rowTotal}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#94a3b8' }}>
              <span>Intensity:</span>
              {[0.1, 0.3, 0.5, 0.7, 0.9].map((pct, i) => (
                <div key={i} style={{ width: 16, height: 16, borderRadius: 3, background: `hsla(217, 70%, ${Math.round(85 - pct*42)}%, 1)` }} />
              ))}
              <span>Higher</span>
            </div>
          </Card>
        </Col>
      </Row>

      {/* ── Site Comparison ── */}
      <div style={{ marginTop: 24, marginBottom: 12 }}>
        <Text strong style={{ fontSize: 14, color: '#0f172a' }}>🔍 Site Comparison</Text>
        <Text style={{ fontSize: 12, color: '#64748b', display: 'block', marginTop: 2 }}>
          Compare all your sites across key metrics: visitors, clicks, engagement, and revenue. 
          {totalRevenue > 0 && totalVisitors > 0 && `Your average revenue per visitor is £${(totalRevenue / totalVisitors).toFixed(2)}. `}
          {highPerformers > 0 && `${highPerformers} sites have engagement above 60%.`}
        </Text>
      </div>
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24}>
          <Card title="Site Comparison" style={{ borderRadius: 16 }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    <th style={{ textAlign: 'left', padding: '12px 10px', fontSize: 11, color: '#64748b', fontWeight: 600, borderBottom: '2px solid #e2e8f0' }}>Website</th>
                    <th style={{ textAlign: 'left', padding: '12px 10px', fontSize: 11, color: '#64748b', fontWeight: 600, borderBottom: '2px solid #e2e8f0' }}>Type</th>
                    <th style={{ textAlign: 'right', padding: '12px 10px', fontSize: 11, color: '#64748b', fontWeight: 600, borderBottom: '2px solid #e2e8f0' }}>Visitors</th>
                    <th style={{ textAlign: 'right', padding: '12px 10px', fontSize: 11, color: '#64748b', fontWeight: 600, borderBottom: '2px solid #e2e8f0' }}>Pageviews</th>
                    <th style={{ textAlign: 'center', padding: '12px 10px', fontSize: 11, color: '#64748b', fontWeight: 600, borderBottom: '2px solid #e2e8f0' }}>Engagement</th>
                    <th style={{ textAlign: 'right', padding: '12px 10px', fontSize: 11, color: '#64748b', fontWeight: 600, borderBottom: '2px solid #e2e8f0' }}>Revenue</th>
                    <th style={{ textAlign: 'center', padding: '12px 10px', fontSize: 11, color: '#64748b', fontWeight: 600, borderBottom: '2px solid #e2e8f0' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredWebsiteAnalytics.slice(0, 10).map((site, idx) => {
                    const eng = 100 - site.bounce_rate;
                    return (
                      <tr key={site.id || idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '10px', color: '#0f172a', fontWeight: 500, maxWidth: 200 }}>
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {site.website_url.replace(/^https?:\/\//, '').replace(/\/$/, '').substring(0, 30)}
                          </div>
                        </td>
                        <td style={{ padding: '10px' }}>
                          <Tag style={{ fontSize: 10, margin: 0, textTransform: 'capitalize' }}>{site.website_type || 'other'}</Tag>
                        </td>
                        <td style={{ padding: '10px', textAlign: 'right', color: PALETTE.cyan[1], fontWeight: 500 }}>
                          {site.unique_visitors.toLocaleString()}
                        </td>
                        <td style={{ padding: '10px', textAlign: 'right', color: PALETTE.blue[1], fontWeight: 500 }}>
                          {site.pageviews.toLocaleString()}
                        </td>
                        <td style={{ padding: '10px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
                            <div style={{ width: 60, height: 6, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                              <div style={{ width: `${eng}%`, height: '100%', background: eng > 60 ? PALETTE.green[1] : eng > 40 ? PALETTE.amber[1] : PALETTE.rose[1], borderRadius: 3 }} />
                            </div>
                            <span style={{ fontSize: 11, fontWeight: 500, color: eng > 60 ? PALETTE.green[1] : eng > 40 ? PALETTE.amber[1] : PALETTE.rose[1] }}>
                              {eng.toFixed(0)}%
                            </span>
                          </div>
                        </td>
                        <td style={{ padding: '10px', textAlign: 'right', color: site.revenue > 0 ? PALETTE.green[0] : '#94a3b8', fontWeight: 500 }}>
                          {site.revenue > 0 ? `$${site.revenue.toLocaleString()}` : '—'}
                        </td>
                        <td style={{ padding: '10px', textAlign: 'center' }}>
                          <Tag color={eng > 60 ? 'green' : eng > 40 ? 'orange' : 'red'} style={{ fontSize: 10, margin: 0 }}>
                            {eng > 60 ? 'Excellent' : eng > 40 ? 'Good' : 'Needs Work'}
                          </Tag>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* Summary Stats */}
            <Row gutter={[12, 12]} style={{ marginTop: 20 }}>
              <Col xs={12} sm={6}>
                <div style={{ textAlign: 'center', padding: 14, background: '#eff6ff', borderRadius: 10, border: '1px solid #bfdbfe' }}>
                  <div style={{ fontSize: 11, color: '#1e40af' }}>Total Visitors</div>
                  <div style={{ fontSize: 22, fontWeight: 600, color: '#1e3a8a' }}>{totalVisitors.toLocaleString()}</div>
                </div>
              </Col>
              <Col xs={12} sm={6}>
                <div style={{ textAlign: 'center', padding: 14, background: '#f0fdf4', borderRadius: 10, border: '1px solid #bbf7d0' }}>
                  <div style={{ fontSize: 11, color: '#166534' }}>Total Revenue</div>
                  <div style={{ fontSize: 22, fontWeight: 600, color: '#14532d' }}>${totalRevenue.toLocaleString()}</div>
                </div>
              </Col>
              <Col xs={12} sm={6}>
                <div style={{ textAlign: 'center', padding: 14, background: '#fef3c7', borderRadius: 10, border: '1px solid #fcd34d' }}>
                  <div style={{ fontSize: 11, color: '#92400e' }}>Avg Engagement</div>
                  <div style={{ fontSize: 22, fontWeight: 600, color: '#78350f' }}>{Math.round(avgEngagement)}%</div>
                </div>
              </Col>
              <Col xs={12} sm={6}>
                <div style={{ textAlign: 'center', padding: 14, background: '#fdf4ff', borderRadius: 10, border: '1px solid #e9d5ff' }}>
                  <div style={{ fontSize: 11, color: '#6b21a8' }}>Rev/Visitor</div>
                  <div style={{ fontSize: 22, fontWeight: 600, color: '#4c1d95' }}>
                    ${totalVisitors > 0 ? (totalRevenue / totalVisitors).toFixed(2) : '0.00'}
                  </div>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* ── Top Websites Table + Funnel ── */}
      <div style={{ marginTop: 24, marginBottom: 12 }}>
        <Text strong style={{ fontSize: 14, color: '#0f172a' }}>🏆 Top Performers & Conversion</Text>
        <Text style={{ fontSize: 12, color: '#64748b', display: 'block', marginTop: 2 }}>
          Your best sites and where visitors drop off in the conversion journey. 
          {filteredWebsiteAnalytics.length > 0 && filteredWebsiteAnalytics[0]?.bounce_rate < 40 
            ? `🌟 ${filteredWebsiteAnalytics[0]?.website_url?.replace(/^https?:\/\//, '').substring(0, 20)} is your star performer.`
            : 'Review sites marked "Needs Work" to improve conversion rates.'}
        </Text>
      </div>
      <Row gutter={[16, 16]} style={{ marginTop: 16, marginBottom: 24 }}>
        <Col xs={24} lg={14}>
          <Card title="Top Performing Websites" style={{ borderRadius: 16 }}>
            <Table
              size="small"
              pagination={false}
              dataSource={filteredWebsiteAnalytics.slice(0, 8)}
              rowKey="id"
              columns={[
                {
                  title: 'Website',
                  dataIndex: 'website_url',
                  key: 'website_url',
                  render: (url, record) => (
                    <div>
                      <div style={{ fontWeight: 400, fontSize: 13 }}>{url.replace(/^https?:\/\//, '').substring(0, 28)}</div>
                      <Tag style={{ fontSize: 10, margin: '2px 0 0' }} color={record.bounce_rate < 40 ? 'green' : record.bounce_rate < 70 ? 'orange' : 'red'}>
                        {record.bounce_rate < 40 ? 'Excellent' : record.bounce_rate < 70 ? 'Good' : 'Needs Work'}
                      </Tag>
                    </div>
                  )
                },
                {
                  title: 'Views',
                  dataIndex: 'pageviews',
                  key: 'pageviews',
                  width: 80,
                  render: val => <span style={{ fontWeight: 400, color: PALETTE.blue[1] }}>{val.toLocaleString()}</span>
                },
                {
                  title: 'Visitors',
                  dataIndex: 'unique_visitors',
                  key: 'unique_visitors',
                  width: 80,
                  render: val => <span style={{ fontWeight: 400, color: PALETTE.cyan[1] }}>{val.toLocaleString()}</span>
                },
                {
                  title: 'Engagement',
                  key: 'engagement',
                  width: 100,
                  render: (_, record) => {
                    const eng = 100 - record.bounce_rate;
                    return (
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 400, marginBottom: 3 }}>{eng.toFixed(1)}%</div>
                        <Progress percent={parseFloat(eng.toFixed(1))} size="small" showInfo={false}
                          strokeColor={eng > 60 ? PALETTE.green[1] : eng > 40 ? PALETTE.amber[1] : PALETTE.rose[1]} />
                      </div>
                    );
                  }
                },
                {
                  title: 'Revenue',
                  dataIndex: 'revenue',
                  key: 'revenue',
                  width: 90,
                  render: val => <span style={{ fontWeight: 400, color: val > 0 ? PALETTE.green[0] : '#94a3b8' }}>{val > 0 ? `$${val.toLocaleString()}` : '—'}</span>
                }
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card title="Conversion Funnel" style={{ borderRadius: 16, height: '100%' }}>
            {['Landing Page', 'Engaged Visit', 'Form/CTA Click', 'Conversion'].map((step, idx) => {
              const base = Math.max(1, totalVisitors || 1000);
              const val = Math.floor(base * Math.pow(0.68, idx));
              const pct = ((val / base) * 100).toFixed(1);
              const drop = idx > 0 ? (100 - (val / Math.floor(base * Math.pow(0.68, idx-1))) * 100).toFixed(1) : null;
              const colors = [PALETTE.blue[1], PALETTE.cyan[1], PALETTE.purple[1], PALETTE.green[1]];
              return (
                <div key={step} style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5, fontSize: 13 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 6, height: 6, borderRadius: '50%', background: colors[idx] }} />
                      <span style={{ fontWeight: 400, color: '#0f172a' }}>{step}</span>
                    </div>
                    <span style={{ color: '#64748b', display: 'flex', gap: 8, fontSize: 12 }}>
                      <span style={{ fontWeight: 400, color: '#0f172a' }}>{val.toLocaleString()}</span>
                      {drop && <span style={{ color: PALETTE.rose[1], fontWeight: 400 }}>↓{drop}%</span>}
                    </span>
                  </div>
                  <div style={{ height: 32, background: '#f8fafc', borderRadius: 8, overflow: 'hidden', position: 'relative' }}>
                    <div style={{
                      height: '100%',
                      width: `${pct}%`,
                      background: `linear-gradient(90deg, ${colors[idx]}, ${colors[idx]}bb)`,
                      borderRadius: 8,
                      display: 'flex', alignItems: 'center',
                      paddingLeft: 10,
                      color: '#fff', fontSize: 11, fontWeight: 400,
                      boxShadow: `0 0 12px ${colors[idx]}55`,
                      transition: 'width 1s ease',
                    }}>
                      {Number(pct) > 12 && `${pct}%`}
                    </div>
                  </div>
                </div>
              );
            })}
            <div style={{ marginTop: 20, padding: 14, background: '#f8fafc', borderRadius: 12 }}>
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>Overall Conversion Rate</div>
              <div style={{ fontSize: 28, fontWeight: 400, color: PALETTE.green[0] }}>
                {totalVisitors > 0 ? ((filteredWebsiteAnalytics.reduce((s,i) => s + i.conversion_rate, 0) / filteredWebsiteAnalytics.length || 0)).toFixed(2) : '0.00'}%
              </div>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AdminBODashboard;